'use client';

/**
 * Navigation Hooks
 * Custom hooks for accessing navigation data in components
 *
 * These hooks provide:
 * - Full navigation context access
 * - Position-specific navigation data
 * - Loading and error states
 * - Active item detection
 */

import { useContext, useMemo } from 'react';
import { NavigationContext } from './NavigationProvider';
import type {
  NavigationContextValue,
  ResolvedNavItem,
  ResolvedNavGroup,
} from './types';

// ============================================================================
// Core Hook
// ============================================================================

/**
 * Access the full navigation context
 *
 * @throws Error if used outside NavigationProvider
 *
 * @example
 * const { navigation, isLoading, error, refresh } = useNavigation();
 */
export function useNavigation(): NavigationContextValue {
  const context = useContext(NavigationContext);

  if (!context) {
    throw new Error('useNavigation must be used within a NavigationProvider');
  }

  return context;
}

/**
 * Access navigation context with optional null return (for conditional usage)
 *
 * @returns Navigation context or null if not in provider
 *
 * @example
 * const navigation = useNavigationOptional();
 * if (!navigation) return null;
 */
export function useNavigationOptional(): NavigationContextValue | null {
  return useContext(NavigationContext);
}

// ============================================================================
// Position-Specific Hooks
// ============================================================================

/**
 * Sidebar navigation data hook
 *
 * @returns Sidebar groups, expansion state, and toggle function
 *
 * @example
 * const { groups, expandedGroups, toggleGroup, isLoading } = useSidebarNavigation();
 */
export function useSidebarNavigation(): {
  groups: ResolvedNavGroup[];
  expandedGroups: Set<string>;
  toggleGroup: (groupKey: string) => void;
  setExpandedGroups: (groups: Set<string>) => void;
  isLoading: boolean;
  error: Error | null;
  refresh: () => Promise<void>;
} {
  const {
    navigation,
    expandedGroups,
    toggleGroup,
    setExpandedGroups,
    isLoading,
    error,
    refresh,
  } = useNavigation();

  return useMemo(
    () => ({
      groups: navigation?.byPosition.sidebar ?? [],
      expandedGroups,
      toggleGroup,
      setExpandedGroups,
      isLoading,
      error,
      refresh,
    }),
    [navigation, expandedGroups, toggleGroup, setExpandedGroups, isLoading, error, refresh]
  );
}

/**
 * Main navigation bar items hook
 *
 * @returns Main navigation items and loading state
 *
 * @example
 * const { items, isLoading } = useMainNavigation();
 */
export function useMainNavigation(): {
  items: ResolvedNavItem[];
  isLoading: boolean;
  error: Error | null;
} {
  const { navigation, isLoading, error } = useNavigation();

  return useMemo(
    () => ({
      items: navigation?.byPosition.main ?? [],
      isLoading,
      error,
    }),
    [navigation, isLoading, error]
  );
}

/**
 * Mobile navigation items hook
 *
 * @returns Mobile navigation items and loading state
 *
 * @example
 * const { items, isLoading } = useMobileNavigation();
 */
export function useMobileNavigation(): {
  items: ResolvedNavItem[];
  isLoading: boolean;
  error: Error | null;
} {
  const { navigation, isLoading, error } = useNavigation();

  return useMemo(
    () => ({
      items: navigation?.byPosition.mobile ?? [],
      isLoading,
      error,
    }),
    [navigation, isLoading, error]
  );
}

/**
 * User menu navigation hook
 *
 * @returns User menu groups and loading state
 *
 * @example
 * const { groups, isLoading } = useUserMenuNavigation();
 */
export function useUserMenuNavigation(): {
  groups: ResolvedNavGroup[];
  isLoading: boolean;
  error: Error | null;
} {
  const { navigation, isLoading, error } = useNavigation();

  return useMemo(
    () => ({
      groups: navigation?.byPosition.userMenu ?? [],
      isLoading,
      error,
    }),
    [navigation, isLoading, error]
  );
}

/**
 * Footer navigation items hook
 *
 * @returns Footer navigation items and loading state
 *
 * @example
 * const { items, isLoading } = useFooterNavigation();
 */
export function useFooterNavigation(): {
  items: ResolvedNavItem[];
  isLoading: boolean;
  error: Error | null;
} {
  const { navigation, isLoading, error } = useNavigation();

  return useMemo(
    () => ({
      items: navigation?.byPosition.footer ?? [],
      isLoading,
      error,
    }),
    [navigation, isLoading, error]
  );
}

// ============================================================================
// Utility Hooks
// ============================================================================

/**
 * Get currently active navigation item
 *
 * @returns Active item or null
 *
 * @example
 * const activeItem = useActiveNavItem();
 */
export function useActiveNavItem(): ResolvedNavItem | null {
  const { navigation, activePath } = useNavigation();

  return useMemo(() => {
    if (!navigation) return null;

    // Find item matching current path
    return (
      navigation.allItems.find((item) => item.isActive) ??
      navigation.allItems.find((item) => item.path === activePath) ??
      null
    );
  }, [navigation, activePath]);
}

/**
 * Get navigation breadcrumb trail based on active item
 *
 * @returns Array of items from root to active item
 *
 * @example
 * const breadcrumbs = useNavBreadcrumbs();
 */
export function useNavBreadcrumbs(): ResolvedNavItem[] {
  const { navigation } = useNavigation();
  const activeItem = useActiveNavItem();

  return useMemo(() => {
    if (!navigation || !activeItem) return [];

    const breadcrumbs: ResolvedNavItem[] = [];
    let current: ResolvedNavItem | undefined = activeItem;

    // Build trail from active item to root
    while (current) {
      breadcrumbs.unshift(current);
      if (current.parentKey) {
        current = navigation.allItems.find((item) => item.key === current!.parentKey);
      } else {
        current = undefined;
      }
    }

    return breadcrumbs;
  }, [navigation, activeItem]);
}

/**
 * Find navigation item by key
 *
 * @param key - Item key to find
 * @returns Item or null
 *
 * @example
 * const settingsItem = useNavItemByKey('settings');
 */
export function useNavItemByKey(key: string): ResolvedNavItem | null {
  const { navigation } = useNavigation();

  return useMemo(() => {
    if (!navigation) return null;
    return navigation.allItems.find((item) => item.key === key) ?? null;
  }, [navigation, key]);
}

/**
 * Find navigation items by group key
 *
 * @param groupKey - Group key to find items for
 * @returns Array of items
 *
 * @example
 * const adminItems = useNavItemsByGroup('admin');
 */
export function useNavItemsByGroup(groupKey: string): ResolvedNavItem[] {
  const { navigation } = useNavigation();

  return useMemo(() => {
    if (!navigation) return [];
    return navigation.allItems.filter((item) => item.groupKey === groupKey);
  }, [navigation, groupKey]);
}

/**
 * Find navigation group by key
 *
 * @param key - Group key to find
 * @returns Group or null
 *
 * @example
 * const adminGroup = useNavGroupByKey('admin');
 */
export function useNavGroupByKey(key: string): ResolvedNavGroup | null {
  const { navigation } = useNavigation();

  return useMemo(() => {
    if (!navigation) return null;
    return navigation.groups.find((group) => group.key === key) ?? null;
  }, [navigation, key]);
}

/**
 * Check if an item path matches the current active path
 *
 * @param path - Path to check
 * @returns Whether path is active
 *
 * @example
 * const isActive = useIsNavPathActive('/settings');
 */
export function useIsNavPathActive(path: string): boolean {
  const { navigation, activePath } = useNavigation();

  return useMemo(() => {
    if (!navigation) return false;

    // Exact match
    if (path === activePath) return true;

    // Prefix match for nested routes
    if (activePath.startsWith(path + '/')) return true;

    // Check if any item with this path is active
    const item = navigation.allItems.find((i) => i.path === path);
    return item?.isActive ?? false;
  }, [navigation, activePath, path]);
}

/**
 * Get simplified/full view toggle
 *
 * @returns Simplified view state and toggle function
 *
 * @example
 * const { isSimplified, toggle } = useSimplifiedNavigation();
 */
export function useSimplifiedNavigation(): {
  isSimplified: boolean;
  setSimplified: (simplified: boolean) => void;
  toggle: () => void;
} {
  const { simplifiedView, setSimplifiedView } = useNavigation();

  return useMemo(
    () => ({
      isSimplified: simplifiedView,
      setSimplified: setSimplifiedView,
      toggle: () => setSimplifiedView(!simplifiedView),
    }),
    [simplifiedView, setSimplifiedView]
  );
}

/**
 * Search navigation items
 *
 * @param query - Search query
 * @returns Matching items
 *
 * @example
 * const results = useNavSearch('settings');
 */
export function useNavSearch(query: string): ResolvedNavItem[] {
  const { navigation } = useNavigation();

  return useMemo(() => {
    if (!navigation || !query.trim()) return [];

    const lowerQuery = query.toLowerCase();

    return navigation.allItems.filter((item) => {
      // Don't include hidden items in search
      if (item.hidden) return false;

      // Search in label, short label, and description
      return (
        item.label.toLowerCase().includes(lowerQuery) ||
        item.shortLabel?.toLowerCase().includes(lowerQuery) ||
        item.description?.toLowerCase().includes(lowerQuery) ||
        item.path.toLowerCase().includes(lowerQuery)
      );
    });
  }, [navigation, query]);
}

/**
 * Get navigation refresh function
 *
 * @returns Refresh function and loading state
 *
 * @example
 * const { refresh, isRefreshing } = useNavRefresh();
 * await refresh();
 */
export function useNavRefresh(): {
  refresh: () => Promise<void>;
  isRefreshing: boolean;
} {
  const { refresh, isLoading } = useNavigation();

  return useMemo(
    () => ({
      refresh,
      isRefreshing: isLoading,
    }),
    [refresh, isLoading]
  );
}
